import React, { useState, useEffect, useMemo } from 'react';
import {
  Receipt,
  Plus,
  Printer,
  CheckCircle,
  XCircle,
  Search,
  X,
  Activity,
  FileCheck,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import {
  HITZ_SELLER,
  formatBillingAddress,
  itemsFromLineItems,
  openBillingDocument,
  type BillingDocumentKind,
  type BillingSeller,
} from '../../lib/billingDocument';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  subscription: string;
}

interface CatalogItem {
  id: string;
  name: string;
  cycle: string;
  thb: number;
}

interface CompanyInfo {
  name: string;
  nameTh: string;
  taxId?: string;
  branch?: string;
  logoUrl?: string;
  website?: string;
  address: string;
  email?: string;
  phone?: string;
  bank: { name: string; accountNumber: string; accountName: string; logoUrl?: string };
}

interface BillingCustomerRow {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  tax_id?: string | null;
  billing_address?: unknown;
}

interface LineItem {
  name?: string;
  quantity?: number;
  amount?: number;
  product_type?: string;
  tier?: string;
  cycle?: string;
  addon_type?: string;
}

interface InvoiceMetadata {
  product_type?: string;
  tier?: string;
  cycle?: string;
  duration_days?: number | null;
}

interface Invoice {
  id: string;
  invoice_number?: string | null;
  status: 'draft' | 'open' | 'paid' | 'void' | 'refunded' | 'payment_failed';
  currency: string;
  subtotal_amount: number;
  tax_amount: number;
  total_amount: number;
  line_items?: LineItem[];
  metadata?: InvoiceMetadata | null;
  notes?: string | null;
  due_at?: string | null;
  paid_at?: string | null;
  created_at: string;
  user?: { id: string; email?: string; name?: string; subscription?: string } | null;
}

interface InvoiceDetail {
  invoice: Invoice;
  user: { id: string; email?: string; name?: string } | null;
  customer?: BillingCustomerRow | null;
  company: CompanyInfo;
}

const sellerFromCompany = (company?: CompanyInfo): BillingSeller => {
  if (!company) return HITZ_SELLER;
  const logoUrl = company.logoUrl
    ? new URL(company.logoUrl, window.location.origin).toString()
    : HITZ_SELLER.logoUrl;
  // Legal identity stays on HITZ_SELLER so a stale Railway env
  // (empty tax ID, unformatted account) cannot reprint the old form.
  return {
    ...HITZ_SELLER,
    website: company.website ? company.website.replace(/^https?:\/\//, '') : HITZ_SELLER.website,
    email: company.email || HITZ_SELLER.email,
    phone: company.phone || HITZ_SELLER.phone,
    logoUrl,
  };
};

type InvoiceType = 'subscription' | 'addon' | 'custom';

interface CustomItemInput {
  name: string;
  amount: string;
  quantity: string;
}

const baht = (n: number) => `฿${Number(n || 0).toLocaleString()}`;

export const ManualInvoices: React.FC = () => {
  const { t } = useTranslation('admin');
  const token = useAuthStore((state) => state.token);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Catalog + users for the create form
  const [plans, setPlans] = useState<CatalogItem[]>([]);
  const [addons, setAddons] = useState<CatalogItem[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [invType, setInvType] = useState<InvoiceType>('subscription');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [planId, setPlanId] = useState('');
  const [addonId, setAddonId] = useState('');
  const [customItems, setCustomItems] = useState<CustomItemInput[]>([{ name: '', amount: '', quantity: '1' }]);
  const [taxAmount, setTaxAmount] = useState('0');
  const [dueInDays, setDueInDays] = useState('7');
  const [notes, setNotes] = useState('');
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const fetchInvoices = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/admin/billing/invoices?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load invoices');
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error(err);
      toast.error(t('invoices.loadError', { defaultValue: 'Failed to load invoices' }));
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogAndUsers = async () => {
    if (!token) return;
    try {
      const [catalogRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/admin/billing/catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (catalogRes.ok) {
        const c = await catalogRes.json();
        setPlans(c.plans || []);
        setAddons(c.addons || []);
        if (c.plans?.[0]) setPlanId(c.plans[0].id);
        if (c.addons?.[0]) setAddonId(c.addons[0].id);
      }
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }
    } catch (err) {
      console.error('Failed to load catalog/users', err);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchCatalogAndUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredInvoices = invoices.filter((inv) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.user?.name || '').toLowerCase().includes(q) ||
      (inv.user?.email || '').toLowerCase().includes(q) ||
      (inv.line_items?.[0]?.name || '').toLowerCase().includes(q)
    );
  });

  const filteredUsers = useMemo(() => {
    if (!userQuery.trim()) return users.slice(0, 8);
    const q = userQuery.toLowerCase();
    return users
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, userQuery]);

  const previewTotal = useMemo(() => {
    const tax = Math.max(0, Math.round(Number(taxAmount) || 0));
    if (invType === 'subscription') {
      return (plans.find((p) => p.id === planId)?.thb || 0) + tax;
    }
    if (invType === 'addon') {
      return (addons.find((a) => a.id === addonId)?.thb || 0) + tax;
    }
    const sub = customItems.reduce(
      (sum, it) => sum + Math.max(0, Math.round(Number(it.amount) || 0)) * Math.max(1, Math.round(Number(it.quantity) || 1)),
      0,
    );
    return sub + tax;
  }, [invType, planId, addonId, customItems, taxAmount, plans, addons]);

  const resetForm = () => {
    setInvType('subscription');
    setSelectedUser(null);
    setUserQuery('');
    setAddonId(addons[0]?.id || '');
    setPlanId(plans[0]?.id || '');
    setCustomItems([{ name: '', amount: '', quantity: '1' }]);
    setTaxAmount('0');
    setDueInDays('7');
    setNotes('');
  };

  const handleCreate = async () => {
    if (!selectedUser) {
      toast.error(t('invoices.selectUserError', { defaultValue: 'Please select a customer' }));
      return;
    }
    const body: Record<string, unknown> = {
      userId: selectedUser.id,
      type: invType,
      taxAmount: Math.max(0, Math.round(Number(taxAmount) || 0)),
      dueInDays: Math.max(0, Math.round(Number(dueInDays) || 7)),
      notes: notes.trim() || null,
    };
    if (invType === 'subscription') body.planId = planId;
    if (invType === 'addon') body.addonId = addonId;
    if (invType === 'custom') {
      body.customItems = customItems
        .filter((it) => it.name.trim() && Number(it.amount) > 0)
        .map((it) => ({ name: it.name.trim(), amount: Number(it.amount), quantity: Number(it.quantity) || 1 }));
      if ((body.customItems as unknown[]).length === 0) {
        toast.error(t('invoices.customItemsError', { defaultValue: 'Add at least one valid line item' }));
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/admin/billing/invoices`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
      toast.success(t('invoices.created', { defaultValue: 'Invoice created' }));
      setShowCreate(false);
      resetForm();
      await fetchInvoices();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (invoice: Invoice, action: 'mark-paid' | 'mark-failed') => {
    if (action === 'mark-paid') {
      const ok = window.confirm(
        t('invoices.confirmPaid', {
          defaultValue: 'Mark this invoice as paid? This activates the plan/add-on for the customer.',
        }),
      );
      if (!ok) return;
    }
    try {
      const res = await fetch(`${API_URL}/admin/billing/invoices/${invoice.id}/${action}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(action === 'mark-failed' ? { reason: 'Manual payment confirmation failed' } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Action failed');
      toast.success(
        action === 'mark-paid'
          ? t('invoices.markedPaid', { defaultValue: 'Invoice marked paid' })
          : t('invoices.markedFailed', { defaultValue: 'Invoice marked failed' }),
      );
      await fetchInvoices();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    }
  };

  const handlePrint = async (invoice: Invoice, kind: BillingDocumentKind = 'quotation') => {
    const target = window.open('', '_blank');
    try {
      const res = await fetch(`${API_URL}/admin/billing/invoices/${invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load invoice');
      const { invoice: inv, user, customer, company }: InvoiceDetail = await res.json();
      const paidAt = inv.paid_at || null;
      openBillingDocument(
        {
          kind,
          invoiceRef: inv.invoice_number || `INV-${inv.id.slice(0, 8)}`,
          issuedAt: inv.created_at,
          dueAt: inv.due_at || null,
          paidAt,
          customer: {
            name: customer?.name || user?.name || user?.email || '—',
            email: customer?.email || user?.email,
            phone: customer?.phone || undefined,
            taxId: customer?.tax_id || undefined,
            address: formatBillingAddress(customer?.billing_address) || undefined,
          },
          items: itemsFromLineItems(inv.line_items, inv.metadata, kind === 'receipt' ? paidAt : null),
          total: Number(inv.total_amount) || 0,
          extraCharge: Number(inv.tax_amount) || 0,
          notes: inv.notes || null,
          seller: sellerFromCompany(company),
        },
        { target },
      );
    } catch (err) {
      target?.close();
      toast.error(err instanceof Error ? err.message : 'Failed to load invoice');
    }
  };

  const statusBadge = (status: Invoice['status']) => {
    const map: Record<string, string> = {
      paid: 'bg-green-50 text-green-700',
      open: 'bg-amber-50 text-amber-700',
      payment_failed: 'bg-red-50 text-red-700',
      void: 'bg-gray-100 text-gray-600',
      refunded: 'bg-purple-50 text-purple-700',
      draft: 'bg-gray-50 text-gray-600',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-50 text-gray-700'}`}>
        {t(`invoices.status.${status}`, { defaultValue: status })}
      </span>
    );
  };

  const formatDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">{t('invoices.loading', { defaultValue: 'Loading invoices…' })}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 print:hidden">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Receipt className="w-8 h-8 text-blue-600" />
              {t('invoices.title', { defaultValue: 'Manual Invoices' })}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('invoices.subtitle', {
                defaultValue: 'Issue invoices by hand and activate plans on payment — while the payment gateway is pending.',
              })}
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreate(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            {t('invoices.create', { defaultValue: 'Create Invoice' })}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[260px] relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('invoices.searchPlaceholder', { defaultValue: 'Search by number, customer, or item…' })}
              className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('invoices.status.all', { defaultValue: 'All statuses' })}</option>
            <option value="open">{t('invoices.status.open', { defaultValue: 'Open' })}</option>
            <option value="paid">{t('invoices.status.paid', { defaultValue: 'Paid' })}</option>
            <option value="payment_failed">{t('invoices.status.payment_failed', { defaultValue: 'Failed' })}</option>
            <option value="void">{t('invoices.status.void', { defaultValue: 'Void' })}</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.number', { defaultValue: 'Invoice' })}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.customer', { defaultValue: 'Customer' })}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.item', { defaultValue: 'Item' })}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.amount', { defaultValue: 'Amount' })}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.status', { defaultValue: 'Status' })}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.created', { defaultValue: 'Created' })}</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">{t('invoices.col.actions', { defaultValue: 'Actions' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-mono font-medium text-gray-900">{inv.invoice_number || '—'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{inv.user?.name || inv.user?.email || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{inv.user?.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{inv.line_items?.[0]?.name || '—'}</div>
                      {(inv.line_items?.length || 0) > 1 && (
                        <div className="text-xs text-gray-500">+{(inv.line_items?.length || 0) - 1}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{baht(inv.total_amount)}</div>
                    </td>
                    <td className="px-6 py-4">{statusBadge(inv.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(inv.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePrint(inv)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title={t('invoices.print', { defaultValue: 'Print / PDF' })}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {inv.status === 'paid' && (
                          <button
                            onClick={() => handlePrint(inv, 'receipt')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title={t('invoices.receipt', { defaultValue: 'Receipt' })}
                          >
                            <FileCheck className="w-4 h-4" />
                          </button>
                        )}
                        {(inv.status === 'open' || inv.status === 'payment_failed') && (
                          <>
                            <button
                              onClick={() => handleAction(inv, 'mark-paid')}
                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              {t('invoices.markPaid', { defaultValue: 'Mark paid' })}
                            </button>
                            {inv.status === 'open' && (
                              <button
                                onClick={() => handleAction(inv, 'mark-failed')}
                                className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                {t('invoices.markFailed', { defaultValue: 'Fail' })}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredInvoices.length === 0 && (
            <div className="text-center py-16">
              <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('invoices.empty', { defaultValue: 'No invoices yet' })}</p>
              <p className="text-gray-400 text-sm">{t('invoices.emptyHint', { defaultValue: 'Create your first manual invoice to get started.' })}</p>
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">{t('invoices.create', { defaultValue: 'Create Invoice' })}</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Customer picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.customer', { defaultValue: 'Customer' })}</label>
                {selectedUser ? (
                  <div className="flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2.5">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{selectedUser.name}</div>
                      <div className="text-xs text-gray-500">{selectedUser.email} · {selectedUser.subscription}</div>
                    </div>
                    <button onClick={() => setSelectedUser(null)} className="text-xs text-blue-600 hover:underline">
                      {t('invoices.form.change', { defaultValue: 'Change' })}
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('invoices.form.searchCustomer', { defaultValue: 'Search customer by name or email…' })}
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filteredUsers.length > 0 && (
                      <div className="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                        {filteredUsers.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => {
                              setSelectedUser(u);
                              setUserQuery('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50"
                          >
                            <div className="text-sm font-medium text-gray-900">{u.name}</div>
                            <div className="text-xs text-gray-500">{u.email} · {u.subscription}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.type', { defaultValue: 'Invoice type' })}</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['subscription', 'custom'] as InvoiceType[]).map((tp) => (
                    <button
                      key={tp}
                      onClick={() => setInvType(tp)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        invType === tp ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {t(`invoices.form.type_${tp}`, { defaultValue: tp })}
                    </button>
                  ))}
                </div>
              </div>

              {/* Type-specific */}
              {invType === 'subscription' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.plan', { defaultValue: 'Plan' })}</label>
                  <select
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} — {baht(p.thb)}</option>
                    ))}
                  </select>
                </div>
              )}

              {invType === 'addon' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.addon', { defaultValue: 'Add-on' })}</label>
                  <select
                    value={addonId}
                    onChange={(e) => setAddonId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {addons.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} — {baht(a.thb)}</option>
                    ))}
                  </select>
                </div>
              )}

              {invType === 'custom' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('invoices.form.items', { defaultValue: 'Line items' })}</label>
                  {customItems.map((it, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        placeholder={t('invoices.form.itemName', { defaultValue: 'Description' })}
                        value={it.name}
                        onChange={(e) => {
                          const next = [...customItems];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setCustomItems(next);
                        }}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="number"
                        min={1}
                        placeholder={t('invoices.form.qty', { defaultValue: 'Qty' })}
                        value={it.quantity}
                        onChange={(e) => {
                          const next = [...customItems];
                          next[idx] = { ...next[idx], quantity: e.target.value };
                          setCustomItems(next);
                        }}
                        className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="number"
                        min={0}
                        placeholder={t('invoices.form.unitPrice', { defaultValue: 'THB' })}
                        value={it.amount}
                        onChange={(e) => {
                          const next = [...customItems];
                          next[idx] = { ...next[idx], amount: e.target.value };
                          setCustomItems(next);
                        }}
                        className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {customItems.length > 1 && (
                        <button
                          onClick={() => setCustomItems(customItems.filter((_, i) => i !== idx))}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setCustomItems([...customItems, { name: '', amount: '', quantity: '1' }])}
                    className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> {t('invoices.form.addItem', { defaultValue: 'Add line item' })}
                  </button>
                </div>
              )}

              {/* Tax + due + notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.tax', { defaultValue: 'Tax / VAT (THB)' })}</label>
                  <input
                    type="number"
                    min={0}
                    value={taxAmount}
                    onChange={(e) => setTaxAmount(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.dueInDays', { defaultValue: 'Due in (days)' })}</label>
                  <input
                    type="number"
                    min={0}
                    value={dueInDays}
                    onChange={(e) => setDueInDays(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.form.notes', { defaultValue: 'Notes (optional)' })}</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('invoices.form.notesPlaceholder', { defaultValue: 'Reference, PO number, internal memo…' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Total preview */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-gray-600">{t('invoices.form.total', { defaultValue: 'Total' })}</span>
                <span className="text-2xl font-bold text-gray-900">{baht(previewTotal)}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
                {t('invoices.form.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg font-medium inline-flex items-center gap-2"
              >
                {submitting ? <Activity className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('invoices.form.submit', { defaultValue: 'Create invoice' })}
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default ManualInvoices;
